/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from 'react';
import styled from 'styled-components';
import ModalPortal from 'components/Modal/ModalPortal';
import PostureModalLayout from 'components/Modal/PostureModal/PostureModalLayout';
import useAddDataInBarChart from 'hooks/useAddDataInBarChart';
import { chartColor, color } from 'asserts/styles/colors';
import { commonTooltipOptions } from 'consts/chartOptions';
import { postureLabels } from 'consts/constants';
import { ReactComponent as IconPostureNeckTurtle } from 'asserts/icons/Icon_Posture_Neck_1.svg';
import { ReactComponent as IconPostureNeckTurtleBack } from 'asserts/icons/Icon_Posture_Neck_2.svg';
import { ReactComponent as IconPostureShoulder } from 'asserts/icons/Icon_Posture_Shoulder_1.svg';
import { ReactComponent as IconPosturePelvis } from 'asserts/icons/Icon_Posture_Pelvis_1.svg';
import { ReactComponent as IconPosturePelvisSide } from 'asserts/icons/Icon_Posture_Pelvis_2.svg';
import { ReactComponent as IconPostureKnee } from 'asserts/icons/Icon_Posture_Knee_1.svg';
import { ReactComponent as IconArrowRight } from 'asserts/icons/ic_arrow_right.svg';
import { ReactComponent as IconArrowLeft } from 'asserts/icons/ic_arrow_left.svg';
import { CSSTransition } from 'react-transition-group';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface PostureStatisticsChartProps {
  chartData: PostureData;
}

const PostureStatisticsBarChart = ({
  chartData
}: PostureStatisticsChartProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  const [isOPenModal, setIsOpenModal] = useState<boolean>(false);
  const [titleText, setTitleText] = useState<string>('');
  const [detailText, setDetailText] = useState<string>('');
  const [directions, setDirections] = useState<string[]>([]);
  const [selectedChartType, setSelectedChartType] = useState<string | null>(
    null
  );

  const turtleneck = chartData?.turtleneck || [];
  const neckside = chartData?.neckside || [];
  const shoulderbalance = chartData?.shoulderbalance || [];
  const pelvisbalance = chartData?.pelvisbalance || [];
  const pelvictilt = chartData?.pelvictilt || [];
  const kneeflexion = chartData?.kneeflexion || [];

  const chartType = [
    'turtleneck',
    'neckside',
    'shoulderbalance',
    'pelvisbalance',
    'pelvictilt',
    'kneeflexion'
  ];

  const handleClickOpenModal = (index: number) => {
    setIsOpenModal(true);
    const selectedChartType = chartType[index];

    setSelectedChartType(selectedChartType);
  };

  const handleClickCloseModal = () => {
    setIsOpenModal(false);
  };

  const turtleneckData = [
    turtleneck.frontserious,
    turtleneck.frontseverity,
    turtleneck.frontlight,
    turtleneck.normal,
    turtleneck.backlight,
    turtleneck.backseverity,
    turtleneck.backserious
  ];

  const necksideData = [
    neckside.leftserious,
    neckside.leftseverity,
    neckside.leftlight,
    neckside.normal,
    neckside.rightlight,
    neckside.rightseverity,
    neckside.rightserious
  ];

  const shoulderbalanceData = [
    shoulderbalance.leftserious,
    shoulderbalance.leftseverity,
    shoulderbalance.leftlight,
    shoulderbalance.normal,
    shoulderbalance.rightlight,
    shoulderbalance.rightseverity,
    shoulderbalance.rightserious
  ];

  const pelvisbalanceData = [
    pelvisbalance.leftserious,
    pelvisbalance.leftseverity,
    pelvisbalance.leftlight,
    pelvisbalance.normal,
    pelvisbalance.rightlight,
    pelvisbalance.rightseverity,
    pelvisbalance.rightserious
  ];

  const pelvictiltData = [
    pelvictilt.frontserious,
    pelvictilt.frontseverity,
    pelvictilt.frontlight,
    pelvictilt.normal,
    pelvictilt.backlight,
    pelvictilt.backseverity,
    pelvictilt.backserious
  ];

  const kneeflexionData = [
    kneeflexion.flexionserious,
    kneeflexion.flexionseverity,
    kneeflexion.flexionlight,
    kneeflexion.normal,
    kneeflexion.extensionlight,
    kneeflexion.extensionseverity,
    kneeflexion.extensionserious
  ];

  const postureTypes = [
    {
      title: '목',
      detail: '거북목',
      data: turtleneckData,
      directions: ['전', '후'],
      icon: <IconPostureNeckTurtle />
    },
    {
      title: '목',
      detail: '목측굴',
      data: necksideData,
      directions: ['좌', '우'],
      icon: <IconPostureNeckTurtleBack />
    },
    {
      title: '어깨',
      detail: '어깨균형',
      data: shoulderbalanceData,
      directions: ['좌', '우'],
      icon: <IconPostureShoulder />
    },
    {
      title: '골반',
      detail: '골반균형',
      data: pelvisbalanceData,
      directions: ['좌', '우'],
      icon: <IconPosturePelvis />
    },
    {
      title: '골반',
      detail: '골반경사',
      data: pelvictiltData,
      directions: ['전', '후'],
      icon: <IconPosturePelvisSide />
    },
    {
      title: '무릎',
      detail: '무릎굴곡',
      data: kneeflexionData,
      directions: ['굴', '신'],
      icon: <IconPostureKnee />
    }
  ];

  const { graphRed, graphOrange, graphYellow, graphGreen } = chartColor;

  const options = {
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 37,
        left: 10,
        right: 10
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: commonTooltipOptions
    },
    scales: {
      x: {
        grid: {
          display: false
        },

        ticks: {
          padding: 7,
          color: `${color.white}`,
          font: {
            size: 13,
            family: 'Noto Sans KR'
          }
        }
      },
      y: {
        display: false
      }
    }
  };

  const colors = [
    graphRed,
    graphOrange,
    graphYellow,
    graphGreen,
    graphYellow,
    graphOrange,
    graphRed
  ];

  return (
    <>
      {postureTypes.map((posture, index) => (
        <PostureChart
          data={posture.data}
          key={index}
          icon={posture.icon}
          title={posture.title}
          detail={posture.detail}
          labels={postureLabels}
          colors={colors}
          options={options}
          setTitleText={setTitleText}
          setDetailText={setDetailText}
          setDirections={setDirections}
          directions={posture.directions}
          onClickModal={() => handleClickOpenModal(index)}
        />
      ))}
      <CSSTransition
        in={isOPenModal}
        classNames="modal"
        timeout={300}
        nodeRef={nodeRef}
        unmountOnExit
      >
        <ModalPortal>
          <PostureModalLayout
            labels={postureLabels}
            titleText={titleText}
            directions={directions}
            colors={colors}
            detailText={detailText}
            selectedChartType={selectedChartType}
            onClickModal={handleClickCloseModal}
          />
        </ModalPortal>
      </CSSTransition>
    </>
  );
};

const PostureChart = ({
  icon,
  title,
  detail,
  colors,
  options,
  labels,
  data: chartData,
  onClickModal,
  setTitleText,
  setDetailText,
  setDirections,
  directions
}: any) => {
  const data = {
    labels,
    datasets: [
      {
        label: `${title}Data`,
        data: chartData,
        backgroundColor: colors,
        barThickness: 6,
        borderRadius: 3
      }
    ]
  };

  const handleClickChart = () => {
    setTitleText(title);
    setDetailText(detail);
    setDirections(directions);

    onClickModal();
  };

  const addDataInBarChart = {
    id: 'addDataInBarChart',
    beforeDatasetsDraw: useAddDataInBarChart()
  };

  return (
    <Wrapper onClick={handleClickChart}>
      <TitleWrapper>
        {icon}
        <TextBox>
          <PositionTitle>{title}</PositionTitle>
          <PositionDetail>{detail}</PositionDetail>
        </TextBox>
      </TitleWrapper>
      <ChartWrapper>
        <Bar data={data} options={options} plugins={[addDataInBarChart]} />
      </ChartWrapper>
      <DirectionIconWrapper>
        <div>
          <IconArrowLeft />
          <p>{directions[0]}</p>
        </div>
        <div className="right">
          <p>{directions[1]}</p>
          <IconArrowRight />
        </div>
      </DirectionIconWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  margin-bottom: 16px;
  background: ${color.white005};
  border-radius: 16px;
  width: 395px;
  height: 288px;
  cursor: pointer;
`;

const TitleWrapper = styled.div`
  display: flex;
  padding: 12px;
`;

const ChartWrapper = styled.div`
  display: flex;
  justify-content: center;
  height: 170px;
`;

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 12px;
  text-align: left;
`;

const PositionTitle = styled.p``;

const PositionDetail = styled.p`
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.3px;
  color: ${chartColor.graphMobile};
`;

const DirectionIconWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 395px;

  & div {
    display: flex;
    align-items: center;
    justify-content: end;
    width: 45%;
    text-align: right;

    & p {
      line-height: 24px;
    }

    &.right {
      justify-content: start;

      & p {
        padding: 0 8px 0 0;
      }
    }
  }

  & div p {
    display: inline;
    padding: 0 0 0 8px;
    font-size: 13px;
  }
`;

export default PostureStatisticsBarChart;
